<script setup lang="ts">
import { h, computed, ref } from 'vue'
import { useStore } from 'vuex'
import type { ColumnDef } from '@tanstack/vue-table'
import {formatTime} from '@/utils/datetime'
import {roleLabel} from '@/utils/roles'
import { useCheckProjectAccess } from '@/composables/useCheckAccess'

import { Invite } from '@/types'
import CopyButton  from './CopyButton.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import { ArrowUpDown, Trash2, RotateCcw, CheckCircle, Clock, XCircle, Mail } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const project = computed(() => store.getters['project/current'])
const canDeleteInvite = computed(() => useCheckProjectAccess(project.value, 'project_invite', ['delete']))

const props = defineProps<{
  invites: Invite[]
}>()

const urlFromUuid = (uuid: string) => {
  return `${location.protocol}//${window.location.host}/invite/${uuid}`
}

const confirmDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingDeleteUuid = ref<string | null>(null)

const deleteInvite = (uuid: string) => {
  pendingDeleteUuid.value = uuid
  confirmDialog.value?.open()
}

const confirmDelete = () => {
  if (!pendingDeleteUuid.value) return
  store.dispatch('project/deleteInvite', pendingDeleteUuid.value)
  pendingDeleteUuid.value = null
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'sent':
    case 'processed':
    case 'delivered':
    case 'open':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'sending':
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'pending':
      return 'bg-gray-100 text-gray-800 border-gray-200'
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'sent':
    case 'processed':
    case 'delivered':
    case 'open':
      return CheckCircle
    case 'sending':
      return Mail
    case 'pending':
      return Clock
    case 'failed':
      return XCircle
    default:
      return Clock
  }
}

const getStatusText = (status: string) => {
  switch (status) {
    case 'pending':
      return 'Pending'
    case 'sending':
      return 'Sending'
    case 'sent':
    case 'processed':
    case 'delivered':
    case 'open':
      return 'Sent'
    case 'failed':
      return 'Failed'
    default:
      return status
  }
}

const resend = (uuid: string) => {
  store.dispatch('project/resendInvite', uuid)
}

const columns: ColumnDef<Invite>[] = [
  {
    accessorKey: 'fullname',
    header: ({ column }) => {
      return h(
        Button,
        {
          variant: 'ghost',
          size: 'sm',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Name', h(ArrowUpDown, { class: 'ml-1 h-3 w-3' })]
      )
    },
    cell: ({ row }) => {
      return h(
        'div',
        {
          class: 'whitespace-nowrap overflow-hidden text-ellipsis max-w-[100px]',
          title: row.original.fullname,
          'data-testid': `invite-name-${row.original.uuid}`
        },
        row.original.fullname
      )
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) =>
      h(
        Button,
        {
          variant: 'ghost',
          size: 'sm',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => [
          'Assigned to',
          h(ArrowUpDown, { class: 'ml-1 h-3 w-3' })
        ]
      ),
    cell: ({ row }) => {
      const { email, phone } = row.original

      return h('div', { class: 'text-muted-foreground', 'data-testid': `invite-email-${row.original.uuid}` }, [
        email
          ? h('div', { class: 'whitespace-nowrap flex items-center space-x-2' }, [
              h('span', { class: 'overflow-hidden text-ellipsis max-w-[200px]', title: email }, email),
              h(
                Button,
                {
                  variant: 'ghost',
                  size: 'sm',
                  onClick: () => resend(row.original.uuid),
                  class: 'p-1',
                  title: 'Resend Email'
                },
                () => [
                  h(RotateCcw, { class: 'h-4.5 w-4.5' })
                ]
              )
            ])
          : phone ? null : '-',
        phone
          ? h('div', { class: 'whitespace-nowrap' }, phone)
          : null,
      ])
    },
  },
  {
    accessorKey: 'project_role',
    header: 'Role',
    cell: ({row}) => {
      return h('div', {class: 'text-muted-foreground', 'data-testid': `invite-role-${row.original.uuid}`}, roleLabel(row.getValue<string>('project_role')))
    },
  },
  {
    accessorKey: 'email_status',
    header: 'Email Status',
    cell: ({ row }) => {
      const status = row.getValue('email_status') as string
      const StatusIcon = getStatusIcon(status)

      return h('div', { class: 'flex items-center space-x-1', 'data-testid': `invite-status-${row.original.uuid}` }, [
        h(Badge, {
          class: `${getStatusColor(status)} text-xs px-2 py-1 flex items-center space-x-1`
        }, () => [
          h(StatusIcon, { class: 'h-3 w-3' }),
          h('span', getStatusText(status))
        ])
      ])
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({row}) => {
      const date = formatTime(row.getValue('created_at'), settings.value.tz, 'D MMM YYYY')
      return h('div', {class: 'text-muted-foreground whitespace-nowrap', 'data-testid': `invite-created-${row.original.uuid}`}, date)
    },
  },
  {
    id: 'copy-link',
    header: '',
    cell: ({row}) => {
      return h('div', {class: 'w-[50px]'}, [
        h(CopyButton, {url: urlFromUuid(row.original.uuid)}),
      ])
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      if (!canDeleteInvite.value) return null
      return h(
        Button,
        {
          variant: 'ghost',
          size: 'sm',
          onClick: () => deleteInvite(row.original.uuid),
          'data-testid': `delete-invite-${row.original.uuid}`
        },
        () => [h(Trash2, { class: 'text-red-400 w-4.5 h-4.5' })]
      )
    },
  },
]
</script>

<template>
  <DataTable :columns="columns" :data="invites" data-testid="invites-list" />
  <DialogConfirm
    ref="confirmDialog"
    title="Delete invite"
    description="Are you sure you want to delete this pending invite?"
    confirmText="Delete"
    variant="error"
    @confirm="confirmDelete"
  />
</template>
